(() => {
  'use strict';

  if (window.GranFormatoProductTheme) {
    window.GranFormatoProductTheme.init(document);
    return;
  }

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;

  function showReveal(element) {
    element.classList.add('pm-reveal-visible');
  }

  function initializeReveals(root = document) {
    const elements = root.querySelectorAll('[data-pm-reveal]:not(.pm-reveal-ready)');

    if (!elements.length) return;

    if (motionQuery.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        element.classList.add('pm-reveal-ready');
        showReveal(element);
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            showReveal(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    elements.forEach((element, index) => {
      element.classList.add('pm-reveal-ready');
      element.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      revealObserver.observe(element);
    });
  }

  function pauseMedia(slide) {
    slide?.querySelectorAll('video').forEach((video) => video.pause());
    slide?.querySelectorAll('model-viewer').forEach((model) => {
      if (typeof model.pause === 'function') model.pause();
    });
  }

  function initializeLightbox(gallery) {
    const dialog = gallery.querySelector('[data-pm-lightbox]');
    const triggers = Array.from(gallery.querySelectorAll('[data-pm-zoom-trigger]'));
    if (!dialog || !triggers.length || dialog.dataset.pmLightboxReady === 'true') return;

    const image = dialog.querySelector('[data-pm-lightbox-image]');
    const viewport = dialog.querySelector('[data-pm-lightbox-viewport]');
    const status = dialog.querySelector('[data-pm-zoom-status]');
    const zoomOut = dialog.querySelector('[data-pm-zoom-out]');
    const zoomIn = dialog.querySelector('[data-pm-zoom-in]');
    const reset = dialog.querySelector('[data-pm-zoom-reset]');
    const close = dialog.querySelector('[data-pm-lightbox-close]');
    const zoomLevels = [1, 1.5, 2, 2.5];
    let zoomIndex = 0;
    let returnFocus;

    function renderZoom() {
      const zoom = zoomLevels[zoomIndex];
      image.style.setProperty('--pm-zoom-width', `${zoom * 100}%`);
      status.textContent = `${Math.round(zoom * 100)}%`;
      zoomOut.disabled = zoomIndex === 0;
      zoomIn.disabled = zoomIndex === zoomLevels.length - 1;
    }

    function setZoom(nextIndex) {
      zoomIndex = Math.max(0, Math.min(nextIndex, zoomLevels.length - 1));
      renderZoom();
    }

    function openLightbox(trigger) {
      returnFocus = trigger;
      image.src = trigger.dataset.pmZoomSrc;
      image.alt = trigger.dataset.pmZoomAlt || '';
      setZoom(0);
      viewport.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      dialog.showModal();
      close.focus();
    }

    function closeLightbox() {
      if (dialog.open) dialog.close();
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => openLightbox(trigger));
    });
    zoomOut.addEventListener('click', () => setZoom(zoomIndex - 1));
    zoomIn.addEventListener('click', () => setZoom(zoomIndex + 1));
    reset.addEventListener('click', () => {
      setZoom(0);
      viewport.scrollTo({ top: 0, left: 0, behavior: motionQuery.matches ? 'auto' : 'smooth' });
    });
    close.addEventListener('click', closeLightbox);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeLightbox();
    });
    dialog.addEventListener('close', () => {
      image.removeAttribute('src');
      image.alt = '';
      setZoom(0);
      returnFocus?.focus();
      returnFocus = null;
    });

    renderZoom();
    dialog.dataset.pmLightboxReady = 'true';
  }

  function initializeGallery(gallery) {
    if (gallery.dataset.pmGalleryReady === 'true') return;

    initializeLightbox(gallery);

    const slides = Array.from(gallery.querySelectorAll('[data-pm-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-pm-thumb]'));
    const thumbTrack = gallery.querySelector('[data-pm-thumb-track]');

    if (slides.length < 2 || !thumbs.length || !thumbTrack) {
      gallery.dataset.pmGalleryReady = 'true';
      return;
    }

    let autoScrollFrame = 0;
    let autoScrollTime = 0;
    let autoScrollDirection = 1;
    let interactionTimer = 0;
    let interactionPaused = false;
    let pointerDown = false;
    let pointerStartX = 0;
    let pointerStartScroll = 0;
    let didDrag = false;

    function stopAutoScroll() {
      if (autoScrollFrame) window.cancelAnimationFrame(autoScrollFrame);
      autoScrollFrame = 0;
      autoScrollTime = 0;
    }

    function autoScroll(timestamp) {
      autoScrollFrame = 0;
      if (motionQuery.matches || interactionPaused || document.hidden || thumbTrack.scrollWidth <= thumbTrack.clientWidth + 1) return;
      if (!autoScrollTime) autoScrollTime = timestamp;
      const elapsed = Math.min(timestamp - autoScrollTime, 50);
      autoScrollTime = timestamp;
      const maximum = thumbTrack.scrollWidth - thumbTrack.clientWidth;
      thumbTrack.scrollLeft += elapsed * 0.018 * autoScrollDirection;
      if (thumbTrack.scrollLeft >= maximum - 1) autoScrollDirection = -1;
      if (thumbTrack.scrollLeft <= 1) autoScrollDirection = 1;
      autoScrollFrame = window.requestAnimationFrame(autoScroll);
    }

    function startAutoScroll() {
      if (motionQuery.matches || interactionPaused || autoScrollFrame) return;
      autoScrollFrame = window.requestAnimationFrame(autoScroll);
    }

    function pauseForInteraction(delay = 2200) {
      interactionPaused = true;
      stopAutoScroll();
      window.clearTimeout(interactionTimer);
      interactionTimer = window.setTimeout(() => {
        if (pointerDown || thumbTrack.matches(':hover') || thumbTrack.matches(':focus-within')) return;
        interactionPaused = false;
        startAutoScroll();
      }, delay);
    }

    function selectSlide(index) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        if (!active && !slide.hidden) pauseMedia(slide);
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });

      thumbs[index]?.scrollIntoView({ behavior: motionQuery.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        if (didDrag) {
          event.preventDefault();
          didDrag = false;
          return;
        }
        pauseForInteraction();
        selectSlide(index);
      });
      thumb.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        pauseForInteraction();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + thumbs.length) % thumbs.length;
        thumbs[nextIndex].focus();
        selectSlide(nextIndex);
      });
    });

    thumbTrack.addEventListener('pointerenter', () => {
      window.clearTimeout(interactionTimer);
      interactionPaused = true;
      stopAutoScroll();
    });
    thumbTrack.addEventListener('pointerleave', () => {
      if (!pointerDown && !thumbTrack.matches(':focus-within')) {
        interactionPaused = false;
        startAutoScroll();
      }
    });
    thumbTrack.addEventListener('focusin', () => {
      window.clearTimeout(interactionTimer);
      interactionPaused = true;
      stopAutoScroll();
    });
    thumbTrack.addEventListener('focusout', (event) => {
      if (!thumbTrack.contains(event.relatedTarget)) {
        interactionPaused = false;
        startAutoScroll();
      }
    });
    thumbTrack.addEventListener('wheel', () => pauseForInteraction(), { passive: true });
    thumbTrack.addEventListener('touchstart', () => pauseForInteraction(), { passive: true });
    thumbTrack.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerDown = true;
      didDrag = false;
      pointerStartX = event.clientX;
      pointerStartScroll = thumbTrack.scrollLeft;
      thumbTrack.classList.add('is-dragging');
      pauseForInteraction();
    });
    thumbTrack.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;
      const distance = event.clientX - pointerStartX;
      if (Math.abs(distance) > 4) didDrag = true;
      if (!didDrag) return;
      event.preventDefault();
      if (!thumbTrack.hasPointerCapture(event.pointerId)) thumbTrack.setPointerCapture(event.pointerId);
      thumbTrack.scrollLeft = pointerStartScroll - distance;
    });

    function finishPointer(event) {
      if (!pointerDown) return;
      pointerDown = false;
      thumbTrack.classList.remove('is-dragging');
      if (thumbTrack.hasPointerCapture(event.pointerId)) thumbTrack.releasePointerCapture(event.pointerId);
      pauseForInteraction();
    }

    thumbTrack.addEventListener('pointerup', finishPointer);
    thumbTrack.addEventListener('pointercancel', finishPointer);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoScroll();
      else startAutoScroll();
    });

    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', () => {
        if (motionQuery.matches) stopAutoScroll();
        else startAutoScroll();
      });
    }

    gallery.dataset.pmGalleryReady = 'true';
    startAutoScroll();
  }

  function initializeGalleries(root = document) {
    root.querySelectorAll('[data-pm-gallery]').forEach(initializeGallery);
  }

  function initializeVariantPicker(productRoot) {
    const picker = productRoot.querySelector('[data-gf-variant-picker]');
    if (!picker || picker.dataset.gfVariantReady === 'true') return;

    const options = Array.from(picker.querySelectorAll('[data-gf-variant-option]'));
    const activeVariant = picker.querySelector('[data-gf-active-variant]');
    const price = productRoot.querySelector('[data-gf-price]');
    const priceValue = price?.querySelector('[data-gf-price-value]');
    const comparePrice = price?.querySelector('[data-gf-compare-price]');
    const priceContext = productRoot.querySelector('[data-gf-price-context]');
    const gallery = productRoot.querySelector('[data-pm-gallery]');

    function updatePrice(option) {
      if (priceContext) {
        priceContext.textContent = `Precio de ${option.dataset.variantTitle || ''}`.trim();
      }

      if (!price || !priceValue) return;

      const amount = Number(option.dataset.variantPrice || 0);
      const compareAmount = Number(option.dataset.variantComparePrice || 0);
      const zeroBehavior = price.dataset.zeroPriceBehavior;
      const zeroText = price.dataset.zeroPriceText || '';
      const shouldHide = amount <= 0 && (zeroBehavior === 'hide' || !zeroText);

      price.hidden = shouldHide;
      price.classList.toggle('pm-hero__price--consult', amount <= 0);
      priceValue.textContent = amount > 0 ? option.dataset.variantPriceFormatted : zeroText;

      if (comparePrice) {
        const showCompare = amount > 0 && compareAmount > amount;
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? option.dataset.variantCompareFormatted : '';
      }
    }

    function updateGallery(option) {
      const mediaId = option.dataset.variantMediaId;
      if (!gallery || !mediaId) return;

      const thumbnail = Array.from(gallery.querySelectorAll('[data-pm-thumb]')).find(
        (item) => item.dataset.mediaId === mediaId
      );
      thumbnail?.click();
    }

    function updateUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ ...(window.history.state || {}), variantId }, '', url);
    }

    function selectVariant(option, shouldUpdateUrl = true) {
      options.forEach((item) => {
        const selected = item === option;
        item.closest('.gf-variant-option')?.classList.toggle('is-selected', selected);
        if (selected) item.closest('.gf-variant-option')?.setAttribute('aria-current', 'true');
        else item.closest('.gf-variant-option')?.removeAttribute('aria-current');
      });

      productRoot.dataset.selectedVariantId = option.value;
      if (activeVariant) activeVariant.value = option.value;
      updatePrice(option);
      updateGallery(option);
      if (shouldUpdateUrl) updateUrl(option.value);

      productRoot.dispatchEvent(
        new CustomEvent('gran-formato:variant-change', {
          bubbles: true,
          detail: {
            variantId: option.value,
            title: option.dataset.variantTitle,
            available: option.dataset.variantAvailable === 'true',
            price: Number(option.dataset.variantPrice || 0),
          },
        })
      );
    }

    options.forEach((option) => {
      option.addEventListener('change', () => {
        if (option.checked) selectVariant(option);
      });
    });

    const selectedOption = options.find((option) => option.checked) || options[0];
    if (selectedOption) selectVariant(selectedOption, false);
    picker.dataset.gfVariantReady = 'true';
  }

  function initializeVariantPickers(root = document) {
    root.querySelectorAll('[data-gf-product-root]').forEach(initializeVariantPicker);
  }

  const technicalMobileQuery = window.matchMedia('(max-width: 749px)');

  function normalizeTechnicalLabel(value = '') {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function resolveTechnicalGroup(value = '', mode = 'default') {
    const label = normalizeTechnicalLabel(value);
    if (mode === 'bordado') {
      const embroideryGroups = [
        { title: 'Configuración de bordado', terms: ['modelo', 'cabezal', 'aguja', 'hilo', 'marca'] },
        { title: 'Área y formatos de trabajo', terms: ['area', 'superficie', 'bastidor', 'marco', 'gorra', 'tubular', 'prenda', 'plano', 'zapato', 'media', 'correa'] },
        { title: 'Operación y automatización', terms: ['pantalla', 'computadora', 'control', 'cambio de color', 'corte', 'recorte', 'rotura', 'laser', 'automatic', 'idioma'] },
        { title: 'Velocidad y producción', terms: ['velocidad', 'rpm', 'puntadas/min', 'puntadas por minuto', 'produccion', 'simultaneo', 'rendimiento'] },
        { title: 'Archivos y memoria', terms: ['memoria', 'patron', 'usb', 'formato de archivo', 'transferencia', 'diseno'] },
        { title: 'Eléctrico y empaque', terms: ['electric', 'volt', 'potencia', 'embalaje', 'empaque', 'peso', 'dimension'] },
        { title: 'Aplicaciones y materiales', terms: ['tela', 'tejido', 'material', 'aplicacion', 'adecuado'] },
        { title: 'Información disponible', terms: ['informacion tecnica en actualizacion', 'completando la ficha'] }
      ];

      return embroideryGroups.find((group) => group.terms.some((term) => label.includes(term)))?.title || 'Otros datos técnicos';
    }

    if (mode === 'laminadora') {
      const laminatorGroups = [
        { title: 'Modelo y sistema', terms: ['modelo', 'tipo', 'modo de trabajo', 'operacion', 'control'] },
        { title: 'Ancho y capacidad', terms: ['ancho', 'pelicula', 'espesor', 'grosor', 'rodillo', 'elevacion'] },
        { title: 'Temperatura y calentamiento', terms: ['temperatura', 'calentamiento', 'calefaccion', 'precalentamiento'] },
        { title: 'Velocidad y producción', terms: ['velocidad', 'rendimiento', 'produccion', 'eficiencia'] },
        { title: 'Eléctrico', terms: ['voltaje', 'volt', 'potencia', 'fuerza', 'frecuencia', 'alimentacion'] },
        { title: 'Dimensiones y peso', terms: ['dimension', 'tamano', 'peso', 'embalaje', 'empaque'] },
        { title: 'Funciones y aplicaciones', terms: ['funcion', 'frio', 'caliente', 'laminacion', 'estampado', 'material', 'aplicacion'] }
      ];

      return laminatorGroups.find((group) => group.terms.some((term) => label.includes(term)))?.title || 'Otros datos técnicos';
    }

    if (mode === 'sublimacion') {
      const sublimationGroups = [
        { title: 'Modelo y sistema', terms: ['modelo', 'tipo', 'sistema', 'control', 'operacion'] },
        { title: 'Formato y capacidad', terms: ['ancho', 'area', 'tamano', 'formato', 'placa', 'bandeja', 'estacion', 'diametro'] },
        { title: 'Temperatura y calentamiento', terms: ['temperatura', 'calor', 'calentamiento', 'calefaccion', 'precalentamiento'] },
        { title: 'Tiempo y producción', terms: ['tiempo', 'velocidad', 'produccion', 'rendimiento', 'ciclo'] },
        { title: 'Presión y operación', terms: ['presion', 'neumatic', 'hidraulic', 'manual', 'automatic', 'aire'] },
        { title: 'Eléctrico', terms: ['voltaje', 'volt', 'potencia', 'frecuencia', 'alimentacion', 'consumo'] },
        { title: 'Dimensiones y peso', terms: ['dimension', 'medida', 'peso', 'embalaje', 'empaque'] },
        { title: 'Aplicaciones y materiales', terms: ['material', 'aplicacion', 'textil', 'tela', 'prenda', 'taza', 'gorra', 'transferencia', 'sublimacion'] }
      ];

      return sublimationGroups.find((group) => group.terms.some((term) => label.includes(term)))?.title || 'Otros datos técnicos';
    }

    if (mode === 'ups') {
      const upsGroups = [
        { title: 'Modelo y sistema', terms: ['modelo', 'tipo', 'sistema', 'tecnologia'] },
        { title: 'Capacidad y potencia', terms: ['capacidad', 'potencia', 'kva', 'volt-amper', 'watt', 'carga'] },
        { title: 'Entrada eléctrica', terms: ['entrada', 'rango de voltaje', 'voltaje de entrada', 'frecuencia de entrada'] },
        { title: 'Salida eléctrica', terms: ['salida', 'voltaje de salida', 'toma', 'enchufe', 'receptaculo'] },
        { title: 'Respaldo y batería', terms: ['bateria', 'autonomia', 'respaldo', 'recarga', 'tiempo de respaldo'] },
        { title: 'Protección y control', terms: ['proteccion', 'regulacion', 'regulador', 'avr', 'sobrecarga', 'cortocircuito', 'alarma', 'pantalla'] },
        { title: 'Dimensiones y peso', terms: ['dimension', 'medida', 'tamano', 'peso', 'embalaje', 'empaque'] },
        { title: 'Aplicaciones y compatibilidad', terms: ['aplicacion', 'compatibilidad', 'equipo', 'impresora', 'maquina', 'uso'] }
      ];

      return upsGroups.find((group) => group.terms.some((term) => label.includes(term)))?.title || 'Otros datos técnicos';
    }

    const groups = [
      { title: 'Configuraciones disponibles', terms: ['configur', 'modelo', 'variante', 'opcion'] },
      { title: 'Cabezal', terms: ['cabezal', 'printhead', 'head'] },
      { title: 'Tinta', terms: ['tinta', 'ink', 'cmyk', 'color'] },
      { title: 'Materiales / tipo', terms: ['material', 'sustrato', 'medio', 'tipo de impresion', 'tecnologia'] },
      { title: 'Velocidad de impresión', terms: ['velocidad', 'pasada', 'pass', 'produccion', 'rendimiento'] },
      { title: 'Calefacción', terms: ['calefac', 'calent', 'secado', 'secador', 'horno', 'infrarro'] },
      { title: 'Recolector de material', terms: ['recolector', 'recogida', 'rebobina', 'enrolla', 'alimentacion'] },
      { title: 'Máximo peso del rollo', terms: ['peso del rollo', 'peso de rollo', 'rollo max', 'capacidad de rollo'] },
      { title: 'Software RIP', terms: ['software', ' rip', 'rip ', 'maintop', 'photoprint', 'wasatch', 'onyx', 'flexi'] },
      { title: 'Entorno operativo', terms: ['entorno', 'ambiente', 'temperatura', 'humedad'] }
    ];

    return groups.find((group) => group.terms.some((term) => label.includes(term)))?.title || 'Otros datos técnicos';
  }

  function createTechnicalDetails(title, content, isOpen = false) {
    const details = document.createElement('details');
    details.className = 'pm-technical-group pm-technical-accordion__item';
    details.dataset.pmTechnicalGroup = '';
    details.open = isOpen;

    const summary = document.createElement('summary');
    summary.className = 'pm-technical-accordion__summary';

    const heading = document.createElement('span');
    heading.className = 'pm-technical-accordion__title';
    heading.textContent = title;

    const icon = document.createElement('span');
    icon.className = 'pm-technical-accordion__icon';
    icon.setAttribute('aria-hidden', 'true');

    const panel = document.createElement('div');
    panel.className = 'pm-technical-accordion__panel';
    panel.append(content);

    summary.append(heading, icon);
    details.append(summary, panel);
    return details;
  }

  function createFallbackRow(cells, headers, fallbackLabel) {
    const row = document.createElement('div');
    row.className = 'pm-fallback-spec-row';

    const term = document.createElement('dt');
    const firstCellText = cells[0]?.textContent?.trim();
    term.textContent = firstCellText || fallbackLabel || 'Dato técnico';

    const description = document.createElement('dd');
    const valueCells = cells.slice(1);
    const usableCells = valueCells.length ? valueCells : cells;

    usableCells.forEach((cell, index) => {
      const value = document.createElement('div');
      value.className = 'pm-fallback-spec-value';
      const header = headers[index + (valueCells.length ? 1 : 0)];

      if (header && normalizeTechnicalLabel(header) !== normalizeTechnicalLabel(term.textContent)) {
        const valueLabel = document.createElement('span');
        valueLabel.className = 'pm-fallback-spec-value__label';
        valueLabel.textContent = header;
        value.append(valueLabel);
      }

      Array.from(cell.childNodes).forEach((node) => value.append(node.cloneNode(true)));
      description.append(value);
    });

    row.append(term, description);
    return row;
  }

  function buildFallbackAccordion(container) {
    if (container.dataset.pmFallbackReady === 'true') return;

    const source = container.querySelector('[data-pm-technical-fallback]');
    const accordion = container.querySelector('[data-pm-fallback-accordion]');
    if (!source || !accordion) return;
    const fallbackMode = container.dataset.pmFallbackMode || 'default';

    const groupedContent = new Map();
    const ensureGroup = (title) => {
      if (!groupedContent.has(title)) {
        const content = document.createElement('div');
        content.className = 'pm-fallback-accordion__content rte';
        groupedContent.set(title, content);
      }
      return groupedContent.get(title);
    };

    const configurations = container.closest('.pm-details--fallback')?.querySelector('.pm-tech-configurations');
    const configurationGrid = configurations?.querySelector('.pm-tech-configurations__grid');
    if (configurationGrid) {
      ensureGroup('Configuraciones disponibles').append(configurationGrid.cloneNode(true));
      configurations.classList.add('is-fallback-accordion-source');
    }

    let currentHeading = '';
    Array.from(source.children).forEach((element) => {
      if (/^H[1-6]$/.test(element.tagName)) {
        currentHeading = element.textContent.trim();
        return;
      }

      if (element.matches('table')) return;
      if (element.querySelector('table')) return;
      const cleanElement = element.cloneNode(true);
      cleanElement.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => heading.remove());
      if (!cleanElement.textContent.trim() && !cleanElement.querySelector('img')) return;

      if (fallbackMode === 'bordado' && cleanElement.matches('ul, ol')) {
        Array.from(cleanElement.children).forEach((item) => {
          if (!item.textContent.trim() && !item.querySelector('img')) return;
          const title = resolveTechnicalGroup(`${currentHeading} ${item.textContent}`, fallbackMode);
          const content = ensureGroup(title);
          const listTag = cleanElement.tagName.toLowerCase();
          let list = content.querySelector(`:scope > ${listTag}.pm-fallback-generated-list`);
          if (!list) {
            list = document.createElement(listTag);
            list.className = 'pm-fallback-generated-list';
            content.append(list);
          }
          list.append(item.cloneNode(true));
        });
        return;
      }

      const title = resolveTechnicalGroup(`${currentHeading} ${cleanElement.textContent}`, fallbackMode);
      ensureGroup(title).append(cleanElement);
    });

    source.querySelectorAll('table').forEach((table) => {
      const rows = Array.from(table.rows);
      if (!rows.length) return;

      const firstRowCells = Array.from(rows[0].cells);
      const firstRowIsHeader = firstRowCells.length > 0 && firstRowCells.every((cell) => cell.tagName === 'TH');
      const headers = firstRowIsHeader ? firstRowCells.map((cell) => cell.textContent.trim()) : [];
      const dataRows = firstRowIsHeader ? rows.slice(1) : rows;
      const nearbyHeading = table.previousElementSibling?.matches('h1, h2, h3, h4, h5, h6')
        ? table.previousElementSibling.textContent.trim()
        : currentHeading;

      dataRows.forEach((tableRow) => {
        const cells = Array.from(tableRow.cells);
        if (!cells.length || cells.every((cell) => !cell.textContent.trim() && !cell.querySelector('img'))) return;

        const label = cells[0]?.textContent?.trim() || nearbyHeading || 'Dato técnico';
        const title = resolveTechnicalGroup(`${nearbyHeading} ${label}`, fallbackMode);
        const list = ensureGroup(title).querySelector('dl') || document.createElement('dl');
        if (!list.parentElement) ensureGroup(title).append(list);
        list.append(createFallbackRow(cells, headers, nearbyHeading));
      });
    });

    groupedContent.forEach((content, title) => {
      if (!content.textContent.trim() && !content.querySelector('img')) return;
      accordion.append(createTechnicalDetails(title, content, accordion.children.length === 0));
    });

    if (accordion.children.length) {
      accordion.hidden = false;
      container.classList.add('is-accordion-ready');
    }

    container.dataset.pmFallbackReady = 'true';
  }

  function initializeTechnicalAccordion(accordion) {
    if (accordion.dataset.pmAccordionReady === 'true') return;
    const groups = Array.from(accordion.querySelectorAll(':scope > [data-pm-technical-group]'));
    if (!groups.length) return;

    let wasMobile = null;
    const syncMode = () => {
      const isMobile = technicalMobileQuery.matches;
      groups.forEach((group, index) => {
        const summary = group.querySelector(':scope > summary');
        if (summary) summary.tabIndex = isMobile ? 0 : -1;
        if (!isMobile) group.open = true;
        else if (wasMobile !== true) group.open = index === 0;
      });
      wasMobile = isMobile;
    };

    syncMode();
    if (typeof technicalMobileQuery.addEventListener === 'function') {
      technicalMobileQuery.addEventListener('change', syncMode);
    }
    accordion.dataset.pmAccordionReady = 'true';
  }

  function initializeTechnicalSheets(root = document) {
    root.querySelectorAll('[data-pm-fallback-specs]').forEach(buildFallbackAccordion);
    root.querySelectorAll('[data-pm-technical-accordion], [data-pm-fallback-accordion]').forEach(initializeTechnicalAccordion);
  }

  function revealAll() {
    document.querySelectorAll('[data-pm-reveal].pm-reveal-ready').forEach(showReveal);
  }

  function init(root = document) {
    initializeGalleries(root);
    initializeVariantPickers(root);
    initializeTechnicalSheets(root);
    initializeReveals(root);
  }

  window.GranFormatoProductTheme = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', (event) => {
      if (event.matches) revealAll();
    });
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
